import type { Permission } from '@/api';
import { useAuthContext } from '@/context/auth-context';
import { ROUTES } from '@/routes';
import { useUserPermissions } from '@pages/queries';
import type { ReactElement } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

interface RoleGuardProps {
  permission?: Permission;
}

export const RoleGuard = ({
  permission,
}: RoleGuardProps = {}): ReactElement => {
  const { isApiClientReady, user } = useAuthContext();

  const { data: permissionsData, isPending: isPermissionsPending } =
    useUserPermissions(
      user?.accountId,
      user?.roles ?? [],
      !!permission && isApiClientReady,
    );

  // No permission required — allow through
  if (!permission) {
    return <Outlet />;
  }

  // Wait for auth initialization
  if (!isApiClientReady) {
    return <></>;
  }

  // Permission-based check — checks permissions from the backend
  if (isPermissionsPending) return <></>;
  if (!permissionsData?.includes(permission)) {
    return (
      <Navigate
        to={ROUTES.Unauthorized.path}
        replace
        state={{ reason: 'feature' }}
      />
    );
  }

  return <Outlet />;
};
